// MyHealthRecords.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  ArrowLeft,
  Search,
  Filter,
  UploadIcon,
  ChevronRight,
  Download,
  Share2,
  X,
  ClipboardX,
  Pill,
  Paperclip,
  Bell,
  ShieldCheck,
  Users,
  Mic,
  Activity,
  FlaskConical,
  Stethoscope,
  FileText,
  LogIn,
  BookOpen,
} from "lucide-react";
import VitalsHistory from "../components/VitalsHistory";
import FeatureHeader from "../components/FeatureHeader";
import Services from "../lib/services";
import { useNavigate } from "react-router-dom";
import Additional_records from "../components/additional_records";
import api from "../lib/api";
import { getCurrentCoordinates } from "../lib/nativeGeolocation";
import { fetchAllConsultationsWithDoctors } from "../utils/appointments";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import jsPDF from "jspdf";
import { deriveQureoScore, getQureoScoreLabel } from "../lib/qureoScore";
import { onHealthDataUpdated } from "../lib/healthSync";
import logo from "../assets/logo.png";

// ============================================================
// HELPER FUNCTIONS
// ============================================================

const getActivityMeta = (title = "") => {
  const t = title.toLowerCase();
  if (t.includes("lab") || t.includes("test")) {
    return {
      icon: <FlaskConical size={18} color="#f59e0b" />,
      bg: "#fff7ed",
      statusColor: "#f59e0b",
    };
  }
  if (t.includes("consultation") || t.includes("doctor")) {
    return {
      icon: <Stethoscope size={18} color="#16a34a" />,
      bg: "#ecfdf5",
      statusColor: "#16a34a",
    };
  }
  if (t.includes("uploaded") || t.includes("record") || t.includes("document")) {
    return {
      icon: <FileText size={18} color="#6366f1" />,
      bg: "#eef2ff",
      statusColor: "#6366f1",
    };
  }
  if (t.includes("read") || t.includes("article")) {
    return {
      icon: <BookOpen size={18} color="#0ea5e9" />,
      bg: "#e0f2fe",
      statusColor: "#0ea5e9",
    };
  }
  if (t.includes("login") || t.includes("signed in")) {
    return {
      icon: <LogIn size={18} color="#64748b" />,
      bg: "#f1f5f9",
      statusColor: "#64748b",
    };
  }
  return {
    icon: <FileText size={18} color="#6b7280" />,
    bg: "#f9fafb",
    statusColor: "#6b7280",
  };
};

// ============================================================
// RECENT RECORDS MODAL COMPONENT
// ============================================================

const RecentRecordsModal = ({
  isOpen,
  onClose,
  pendingTests,
  userDocuments,
  appointments,
  prescriptions,
  navigate,
}) => {
  const allRecords = [
    ...(pendingTests || []).map((test) => ({
      type: "lab",
      title: test.tests?.[0]?.testId?.name || "Lab Test",
      date: test.createdAt,
      status: test.status || "New",
      icon: <FlaskConical size={18} color="#22c55e" />,
      color: "#22c55e",
      bg: "#ecfdf5",
      id: test._id,
      original: test,
    })),
    ...(userDocuments || []).map((doc) => ({
      type: "document",
      title: doc.title || "Medical Record",
      date: doc.createdAt,
      status: "Added",
      icon: <FileText size={18} color="#7c3aed" />,
      color: "#7c3aed",
      bg: "#f5f3ff",
      id: doc._id,
      original: doc,
    })),
    ...([...((appointments || []))]).map((appt) => ({
      type: "consultation",
      title: appt.doctor_?.name || appt.doctor?.name || "Doctor Consultation",
      date: appt.appointmentTime,
      status: appt.status || "Scheduled",
      icon: <Stethoscope size={18} color="#0ea5e9" />,
      color: "#0ea5e9",
      bg: "#f0f9ff",
      id: appt._id,
      original: appt,
    })),
    ...(prescriptions || []).map((rx) => ({
      type: "prescription",
      title: rx.medicineName || "Prescription",
      date: rx.issuedDate || rx.createdAt,
      status: "Rx",
      icon: <ClipboardX size={18} color="#f59e0b" />,
      color: "#f59e0b",
      bg: "#fff7ed",
      id: rx._id,
      original: rx,
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  const getTypeLabel = (type) => {
    const labels = {
      lab: "Lab Test",
      document: "Document",
      consultation: "Consultation",
      prescription: "Prescription",
    };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      New: "#22c55e",
      Added: "#7c3aed",
      Scheduled: "#0ea5e9",
      Completed: "#22c55e",
      Rx: "#f59e0b",
      Pending: "#f59e0b",
    };
    return colors[status] || "#6b7280";
  };

  const handleRecordClick = (record) => {
    onClose();
    if (record.type === "consultation") {
      navigate(`/doctor-details/${record.original.doctor?._id || ""}`);
    } else if (record.type === "lab") {
      navigate(`/booking-details/${record.original._id}`);
    } else if (record.type === "prescription") {
      // Navigate to prescription details or open modal
    } else if (record.type === "document") {
      // Open document preview
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 4000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 20,
          width: "100%",
          maxWidth: 600,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 20px 14px",
            borderBottom: "1px solid #f1f1f1",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              All Recent Records
            </h3>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 12,
                color: "#6b7280",
              }}
            >
              {allRecords.length} record{allRecords.length !== 1 ? "s" : ""} found
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "#f3f4f6",
              borderRadius: "50%",
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#e5e7eb")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#f3f4f6")}
          >
            <X size={18} color="#6b7280" />
          </button>
        </div>

        {/* Records List */}
        <div
          style={{
            padding: "12px 20px 20px",
            overflowY: "auto",
            flex: 1,
          }}
        >
          {allRecords.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 0",
                color: "#9ca3af",
              }}
            >
              <p style={{ fontSize: 14, margin: 0 }}>No records found</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {allRecords.map((item, index) => (
                <div
                  key={item.id || index}
                  onClick={() => handleRecordClick(item)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "12px 14px",
                    borderRadius: 12,
                    background: "#fafafa",
                    border: "1px solid #f3f4f6",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#f5f3ff";
                    e.currentTarget.style.borderColor = "#e5dbff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#fafafa";
                    e.currentTarget.style.borderColor = "#f3f4f6";
                  }}
                >
                  {/* Icon */}
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      background: item.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginRight: 12,
                    }}
                  >
                    {item.icon}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#111827",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.title}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#6b7280",
                        marginTop: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span>{getTypeLabel(item.type)}</span>
                      <span
                        style={{
                          width: 3,
                          height: 3,
                          background: "#d1d5db",
                          borderRadius: "50%",
                        }}
                      />
                      <span>{new Date(item.date).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Status */}
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: getStatusColor(item.status),
                      background: `${getStatusColor(item.status)}15`,
                      padding: "4px 10px",
                      borderRadius: 999,
                      flexShrink: 0,
                      textTransform: "capitalize",
                    }}
                  >
                    {item.status}
                  </div>

                  {/* Chevron */}
                  <ChevronRight
                    size={16}
                    color="#9ca3af"
                    style={{ marginLeft: 8, flexShrink: 0 }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with close button */}
        <div
          style={{
            padding: "12px 20px 16px",
            borderTop: "1px solid #f1f1f1",
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              background: "#fff",
              fontSize: 13,
              fontWeight: 500,
              color: "#6b7280",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f9fafb";
              e.currentTarget.style.borderColor = "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.borderColor = "#e5e7eb";
            }}
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================================
// RECENT RECORDS COMPONENT
// ============================================================

const RecentRecords = ({
  pendingTests,
  userDocuments,
  appointments,
  prescriptions,
  navigate,
  onSeeAllClick,
}) => {
  const records = [
    ...(pendingTests || []).map((test) => ({
      type: "lab",
      title: test.tests?.[0]?.testId?.name || "Lab Test",
      date: test.createdAt,
      status: test.status || "New",
      icon: <FlaskConical size={18} color="#22c55e" />,
      color: "#22c55e",
      bg: "#ecfdf5",
    })),
    ...(userDocuments || []).map((doc) => ({
      type: "document",
      title: doc.title || "Medical Record",
      date: doc.createdAt,
      status: "Added",
      icon: <FileText size={18} color="#7c3aed" />,
      color: "#7c3aed",
      bg: "#f5f3ff",
    })),
    ...([...((appointments || []))]).map((appt) => ({
      type: "consultation",
      title: appt.doctor_?.name || appt.doctor?.name || "Doctor Consultation",
      date: appt.appointmentTime,
      status: appt.status || "Scheduled",
      icon: <Stethoscope size={18} color="#0ea5e9" />,
      color: "#0ea5e9",
      bg: "#f0f9ff",
    })),
    ...(prescriptions || []).map((rx) => ({
      type: "prescription",
      title: rx.medicineName || "Prescription",
      date: rx.issuedDate || rx.createdAt,
      status: "Rx",
      icon: <ClipboardX size={18} color="#f59e0b" />,
      color: "#f59e0b",
      bg: "#fff7ed",
    })),
  ]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 8);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 18,
        padding: 14,
        marginTop: 14,
        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
            Recent Records
          </div>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
            Latest health activity
          </div>
        </div>

        <button
          onClick={onSeeAllClick}
          style={{
            border: "none",
            background: "#f5f3ff",
            color: "#7c3aed",
            borderRadius: 999,
            padding: "6px 10px",
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          See all
        </button>
      </div>

      {!records.length ? (
        <div
          style={{
            textAlign: "center",
            padding: "16px 0",
            color: "#9ca3af",
            fontSize: 13,
          }}
        >
          No recent records
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {records.map((item, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 0",
                borderBottom:
                  index !== records.length - 1 ? "1px solid #f3f4f6" : "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    background: item.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 15,
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#111827",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 170,
                    }}
                  >
                    {item.title}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#6b7280",
                      marginTop: 2,
                    }}
                  >
                    {new Date(item.date).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: item.color,
                  background: item.bg,
                  padding: "4px 8px",
                  borderRadius: 999,
                  flexShrink: 0,
                  textTransform: "capitalize",
                }}
              >
                {item.status}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// AI CARD COMPONENT
// ============================================================

const Aicard = ({
  labRecords,
  consultationRecords,
  prescriptionRecords,
  documentRecords,
  latestAssessment,
  latestVitals,
  latestLabBooking,
  latestAppointment,
}) => {
  const [aiSummary, setAiSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const buildFallbackSummary = useCallback(() => {
    const parts = [];

    if (latestAssessment) {
      const score = latestAssessment.score
        ? `${latestAssessment.score}/100`
        : "available";
      parts.push(`Your latest health assessment score is ${score}.`);
    }

    if (latestVitals) {
      const bloodPressure =
        latestVitals.bloodPressure?.systolic &&
        latestVitals.bloodPressure?.diastolic
          ? `${latestVitals.bloodPressure.systolic}/${latestVitals.bloodPressure.diastolic} mmHg`
          : null;
      const vitalsBits = [
        bloodPressure ? `blood pressure ${bloodPressure}` : null,
        latestVitals.heartRate ? `heart rate ${latestVitals.heartRate} bpm` : null,
        latestVitals.oxygenLevel ? `oxygen ${latestVitals.oxygenLevel}%` : null,
      ].filter(Boolean);

      if (vitalsBits.length) {
        parts.push(`Recent vitals include ${vitalsBits.join(", ")}.`);
      }
    }

    if (labRecords?.length) {
      parts.push(
        `You have ${labRecords.length} lab record${
          labRecords.length > 1 ? "s" : ""
        }, with the latest labeled ${labRecords[0]?.title || "Lab Test"}.`
      );
    }

    if (consultationRecords?.length) {
      parts.push(
        `You have ${consultationRecords.length} consultation record${
          consultationRecords.length > 1 ? "s" : ""
        }, including ${consultationRecords[0]?.title || "a recent doctor visit"}.`
      );
    }

    if (prescriptionRecords?.length) {
      parts.push(
        `There are ${prescriptionRecords.length} prescription record${
          prescriptionRecords.length > 1 ? "s" : ""
        } on file.`
      );
    }

    if (documentRecords?.length) {
      parts.push(
        `You also have ${documentRecords.length} uploaded document${
          documentRecords.length > 1 ? "s" : ""
        }.`
      );
    }

    if (latestLabBooking) {
      parts.push(
        `Your latest lab booking shows ${
          latestLabBooking?.tests?.length || 1
        } test${
          (latestLabBooking?.tests?.length || 1) > 1 ? "s" : ""
        } booked.`
      );
    }

    if (latestAppointment) {
      parts.push(
        `Your latest appointment is currently ${String(
          latestAppointment.status || "scheduled"
        ).toLowerCase()}.`
      );
    }

    if (!parts.length) {
      return "No records are available yet. Once you add consultations, lab results, prescriptions, or documents, a summary will appear here.";
    }

    return parts.join(" ");
  }, [
    latestAssessment,
    latestVitals,
    labRecords,
    consultationRecords,
    prescriptionRecords,
    documentRecords,
    latestLabBooking,
    latestAppointment,
  ]);

  const fetchAISummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const summaryRes = await api.get("/api/metrics/summary").catch(() => null);
      const apiSummary = summaryRes?.data?.summary;

      if (apiSummary) {
        if (typeof apiSummary === "string") {
          setAiSummary(apiSummary);
        } else if (typeof apiSummary === "object") {
          setAiSummary(
            apiSummary.summary ||
              apiSummary.text ||
              apiSummary.overview ||
              buildFallbackSummary()
          );
        } else {
          setAiSummary(buildFallbackSummary());
        }
        return;
      }

      setAiSummary(buildFallbackSummary());
    } catch (e) {
      setAiSummary(buildFallbackSummary());
      setError("");
    } finally {
      setLoading(false);
    }
  }, [buildFallbackSummary]);

  useEffect(() => {
    fetchAISummary();
  }, [fetchAISummary]);

  return (
    <div style={{ padding: "0 10px 12px" }}>
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          background: "linear-gradient(90deg, #f7f4ff 0%, #faf8ff 100%)",
          borderRadius: 18,
          padding: "10px 12px",
          border: "1px solid #efe8ff",
          minHeight: 78,
          width: "100%",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(124,77,255,0.04)",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 8,
            left: 46,
            color: "#c4b5fd",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          ✦
        </span>
        <span
          style={{
            position: "absolute",
            top: 8,
            right: 10,
            color: "#a78bfa",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          ✦
        </span>
        <div style={{ flex: 1, minWidth: 0, marginLeft: 0 }}>
          <div
            style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3 }}
          >
            <span
              style={{ fontWeight: 700, fontSize: 10, color: "#7c4dff", lineHeight: 1 }}
            >
              AI Summary
            </span>
            <span
              style={{
                fontSize: 7,
                fontWeight: 700,
                color: "#7c4dff",
                border: "1px solid #d8c7ff",
                background: "#f5f0ff",
                borderRadius: 6,
                padding: "1px 4px",
                lineHeight: 1.1,
              }}
            >
              AI
            </span>
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#1f2937",
              lineHeight: 1.2,
              marginBottom: 3,
            }}
          >
            {loading ? "Generating summary..." : error ? "AI Error" : "Health Overview"}
          </div>
          <div
            style={{
              fontSize: 9,
              color: "#6b7280",
              lineHeight: 1.35,
              minHeight: 32,
            }}
          >
            {loading
              ? "Please wait while the AI analyzes your records."
              : error
              ? error
              : aiSummary}
          </div>
        </div>
        <button
          style={{
            marginLeft: 10,
            background: "#fff",
            border: "1px solid #ece5ff",
            borderRadius: 999,
            padding: "0 12px",
            height: 30,
            fontWeight: 700,
            fontSize: 10,
            color: "#7c4dff",
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: "0 2px 6px rgba(124,77,255,0.04)",
            display: "flex",
            alignItems: "center",
            gap: 4,
            flexShrink: 0,
            whiteSpace: "nowrap",
            opacity: loading ? 0.6 : 1,
          }}
          onClick={fetchAISummary}
          disabled={loading}
        >
          Regenerate
          <ChevronRight size={13} strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
};

// ============================================================
// SEARCH ROW COMPONENT
// ============================================================

const SearchRow = ({ searchQuery, setSearchQuery, navigate }) => {
  const [isListening, setIsListening] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const recognitionRef = useRef(null);

  const handleMicClick = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognitionRef.current = recognition;
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setLocalSearchQuery(transcript);
      setSearchQuery(transcript);
    };
    recognition.start();
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 6,
          height: 64,
          borderRadius: 32,
          padding: "6px 10px",
          background: "#f7f4ff",
          border: "1px solid #ece5ff",
          boxShadow: "0 2px 8px rgba(124,77,255,0.08)",
        }}
      >
        <div
          onClick={() => navigate("/asha")}
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "#efe8ff",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            cursor: "pointer",
          }}
        >
          <AutoAwesomeIcon style={{ fontSize: 20, color: "var(--primary)" }} />
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 8,
            position: "relative",
            background: "#fff",
            border: "1px solid #eee8ff",
            borderRadius: 26,
            padding: "0 46px 0 12px",
            height: 50,
          }}
        >
          <Search size={17} color="var(--primary)" style={{ flexShrink: 0 }} />
          <input
            type="text"
            placeholder="What do you need help with?"
            value={localSearchQuery}
            onChange={(e) => {
              setLocalSearchQuery(e.target.value);
              setSearchQuery(e.target.value);
            }}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 10,
              color: "#8a8a95",
            }}
          />
          <div
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              height: 22,
              paddingLeft: 10,
              borderLeft: "1px solid #e6e6e8",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Mic
              size={19}
              color="var(--primary)"
              style={{ cursor: "pointer", opacity: isListening ? 0.6 : 1 }}
              onClick={handleMicClick}
            />
          </div>
        </div>
      </div>

   {/* Upload Rx */}
      <button
        type="button"
        aria-label="Upload Prescription"
        onClick={() => navigate("/prescription")}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          width: 40,
          height: 40,
          borderRadius: "50% ",
          border: "1px solid #ece5ff",
          background: "#fff",
          color: "var(--primary)",
          cursor: "pointer",
          flexShrink: 0,
          padding: "2px 0",
          boxShadow: "0 2px 6px rgba(124,77,255,0.06)",
        }}
      >
        <UploadIcon size={18} strokeWidth={2} />
        <span style={{ fontSize: 9, fontWeight: 600, color: "#1a1a2e", lineHeight: 1.1, textAlign: "center" }}>
       
        </span>
      </button>
    </div>
  );
};

// ============================================================
// SHARE RECORDS MODAL COMPONENT
// ============================================================

const ShareRecordsModal = ({ isOpen, onClose, records, navigate, onShare }) => {
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [shareMethod, setShareMethod] = useState("link");
  const [shareEmail, setShareEmail] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [isSharing, setIsSharing] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [copied, setCopied] = useState(false);

  // Toggle record selection
  const toggleRecord = (recordId) => {
    setSelectedRecords(prev => 
      prev.includes(recordId) 
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    );
  };

  // Select all records
  const selectAll = () => {
    if (selectedRecords.length === records.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(records.map(r => r.id || r._id));
    }
  };

  // Generate share link
  const generateShareLink = () => {
    const recordIds = selectedRecords.join(',');
    const baseUrl = window.location.origin;
    const shareableLink = `${baseUrl}/shared-records/${recordIds}?timestamp=${Date.now()}`;
    setShareLink(shareableLink);
    return shareableLink;
  };

  // Copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Share via email
  const shareViaEmail = async () => {
    if (!shareEmail) {
      alert('Please enter an email address');
      return;
    }

    setIsSharing(true);
    try {
      const recordIds = selectedRecords.join(',');
      const shareData = {
        email: shareEmail,
        recordIds: recordIds,
        message: shareMessage || 'I\'m sharing my health records with you.',
        records: records.filter(r => selectedRecords.includes(r.id || r._id))
      };

      // Call API to send email
      const response = await api.post('/api/share-records', shareData);
      
      if (response.data.success) {
        alert(`Records shared successfully with ${shareEmail}!`);
        onClose();
      } else {
        alert('Failed to share records. Please try again.');
      }
    } catch (err) {
      console.error('Share error:', err);
      alert('Error sharing records. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  // Share via native share API
  const shareNative = async () => {
    const recordIds = selectedRecords.join(',');
    const shareableLink = generateShareLink();
    
    const shareData = {
      title: 'My Health Records',
      text: `I'm sharing my health records with you. View them here: ${shareableLink}`,
      url: shareableLink,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
          // Fallback to copy link
          await copyToClipboard(shareableLink);
          alert('Link copied to clipboard!');
        }
      }
    } else {
      await copyToClipboard(shareableLink);
      alert('Link copied to clipboard!');
    }
  };

  // Share to doctor chat
  const shareToDoctorChat = () => {
    if (selectedRecords.length === 0) {
      alert('Please select at least one record to share.');
      return;
    }

    const selectedRecordsData = records.filter(r => selectedRecords.includes(r.id || r._id));
    
    navigate('/consultation-chats', {
      state: {
        sharedRecords: selectedRecordsData,
        shareMessage: shareMessage || 'Sharing my health records with you.',
      }
    });

    onClose();
    onShare && onShare();
  };

  // Handle share action
  const handleShare = async () => {
    if (selectedRecords.length === 0) {
      alert('Please select at least one record to share.');
      return;
    }

    if (shareMethod === 'link') {
      await shareNative();
      onClose();
    } else if (shareMethod === 'email') {
      await shareViaEmail();
    } else if (shareMethod === 'doctor') {
      shareToDoctorChat();
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 5000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 20,
          width: "100%",
          maxWidth: 560,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 20px 14px",
            borderBottom: "1px solid #f1f1f1",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Share Records
            </h3>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 12,
                color: "#6b7280",
              }}
            >
              Select records to share with your care team
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "#f3f4f6",
              borderRadius: "50%",
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#e5e7eb")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#f3f4f6")}
          >
            <X size={18} color="#6b7280" />
          </button>
        </div>

        {/* Share Method Selector */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #f1f1f1",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setShareMethod("link")}
              style={{
                flex: 1,
                padding: "8px 12px",
                borderRadius: 10,
                border: shareMethod === "link" ? "2px solid #704af6" : "1px solid #e5e7eb",
                background: shareMethod === "link" ? "#f5f3ff" : "#fff",
                color: shareMethod === "link" ? "#704af6" : "#6b7280",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              🔗 Share Link
            </button>
            <button
              onClick={() => setShareMethod("email")}
              style={{
                flex: 1,
                padding: "8px 12px",
                borderRadius: 10,
                border: shareMethod === "email" ? "2px solid #704af6" : "1px solid #e5e7eb",
                background: shareMethod === "email" ? "#f5f3ff" : "#fff",
                color: shareMethod === "email" ? "#704af6" : "#6b7280",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              ✉️ Email
            </button>
            <button
              onClick={() => setShareMethod("doctor")}
              style={{
                flex: 1,
                padding: "8px 12px",
                borderRadius: 10,
                border: shareMethod === "doctor" ? "2px solid #704af6" : "1px solid #e5e7eb",
                background: shareMethod === "doctor" ? "#f5f3ff" : "#fff",
                color: shareMethod === "doctor" ? "#704af6" : "#6b7280",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              🩺 Doctor Chat
            </button>
          </div>
        </div>

        {/* Records List */}
        <div
          style={{
            padding: "12px 20px",
            overflowY: "auto",
            flex: 1,
            maxHeight: "300px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>
              {records.length} record{records.length !== 1 ? "s" : ""} available
            </span>
            <button
              onClick={selectAll}
              style={{
                border: "none",
                background: "none",
                color: "#704af6",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {selectedRecords.length === records.length ? "Deselect All" : "Select All"}
            </button>
          </div>

          {records.map((record) => {
            const recordId = record.id || record._id;
            const isSelected = selectedRecords.includes(recordId);
            
            return (
              <div
                key={recordId}
                onClick={() => toggleRecord(recordId)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "10px 12px",
                  borderRadius: 10,
                  marginBottom: 6,
                  background: isSelected ? "#f5f3ff" : "#fafafa",
                  border: isSelected ? "1px solid #704af6" : "1px solid #f3f4f6",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = "#fafafa";
                  }
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    border: isSelected ? "2px solid #704af6" : "2px solid #d1d5db",
                    background: isSelected ? "#704af6" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 12,
                    flexShrink: 0,
                  }}
                >
                  {isSelected && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#111827",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {record.title || record.medicineName || "Record"}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#6b7280",
                    }}
                  >
                    {record.type || record.status || "Health Record"} •{" "}
                    {record.date ? new Date(record.date).toLocaleDateString() : "Recent"}
                  </div>
                </div>
              </div>
            );
          })}

          {records.length === 0 && (
            <div style={{ textAlign: "center", padding: "20px 0", color: "#9ca3af" }}>
              No records available to share
            </div>
          )}
        </div>

        {/* Share Options */}
        {shareMethod === "email" && (
          <div
            style={{
              padding: "12px 20px",
              borderTop: "1px solid #f1f1f1",
              flexShrink: 0,
            }}
          >
            <input
              type="email"
              placeholder="Enter recipient's email"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                fontSize: 14,
                marginBottom: 8,
              }}
            />
            <textarea
              placeholder="Add a message (optional)"
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                fontSize: 14,
                minHeight: 60,
                resize: "vertical",
                marginBottom: 8,
              }}
            />
          </div>
        )}

        {/* Footer Actions */}
        <div
          style={{
            padding: "12px 20px 16px",
            borderTop: "1px solid #f1f1f1",
            display: "flex",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "10px",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              background: "#fff",
              fontSize: 13,
              fontWeight: 500,
              color: "#6b7280",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f9fafb";
              e.currentTarget.style.borderColor = "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.borderColor = "#e5e7eb";
            }}
          >
            Cancel
          </button>
          
          <button
            onClick={handleShare}
            disabled={selectedRecords.length === 0 || isSharing}
            style={{
              flex: 2,
              padding: "10px",
              borderRadius: 10,
              border: "none",
              background: selectedRecords.length === 0 ? "#e5e7eb" : "#704af6",
              color: selectedRecords.length === 0 ? "#9ca3af" : "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: selectedRecords.length === 0 || isSharing ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {isSharing ? (
              <>
                <span className="spinner" style={{
                  display: "inline-block",
                  width: 16,
                  height: 16,
                  border: "2px solid rgba(255,255,255,0.3)",
                  borderTop: "2px solid #fff",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }} />
                Sharing...
              </>
            ) : (
              <>
                <Share2 size={16} />
                Share {selectedRecords.length} Record{selectedRecords.length !== 1 ? "s" : ""}
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Add this CSS to your styles or global CSS
const shareStyles = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function MyHealthRecords() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
// In the main component, add this state:
const [showShareModal, setShowShareModal] = useState(false);

// Add this function to prepare shareable records:


const handleShareClick = () => {
  const shareableRecords = getShareableRecords();
  if (shareableRecords.length === 0) {
    alert("No records available to share.");
    return;
  }
  setShowShareModal(true);
};
  // ===== STATE =====
  const [activeTab, setActiveTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedPrescriptionGroup, setSelectedPrescriptionGroup] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fileUrl, setFileUrl] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    file: null,
  });
  const [viewingFull, setViewingFull] = useState(false);
  const [offlineSaved, setOfflineSaved] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showAllRecordsModal, setShowAllRecordsModal] = useState(false);
  const [isTrustStripPaused, setIsTrustStripPaused] = useState(false);
  const trustStripRef = useRef(null);

  // Data states
  const [appointments, setAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  const [pendingTests, setPendingTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tests, setTests] = useState([]);
  const [completedTests, setCompletedTests] = useState([]);
  const [error, setError] = useState("");
  const [latestVitals, setLatestVitals] = useState(null);
  const [latestAssessment, setLatestAssessment] = useState(null);
  const [userDocuments, setUserDocuments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [medications, setMedications] = useState([]);
  const [medicationsLoading, setMedicationsLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [location, setLocation] = useState({ lat: null, lon: null });
  const [nearbyDoctors, setNearbyDoctors] = useState([]);
  const [loadingNearby, setLoadingNearby] = useState(true);
  const [chatSessions, setChatSessions] = useState([]);
  const [loadingChatSessions, setLoadingChatSessions] = useState(false);

  // User ID
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const user_id = userData?._id || userData?.id || userData;
  const effectiveUserId = user?._id || localStorage.getItem("userId") || user_id;
  const userId = localStorage.getItem("userId");

  // ===== TRUST STRIP AUTO-SCROLL =====
  useEffect(() => {
    const strip = trustStripRef.current;
    if (!strip || isTrustStripPaused) return;
    const interval = setInterval(() => {
      const resetPoint = strip.scrollWidth / 2;
      if (resetPoint <= 0) return;
      if (strip.scrollLeft >= resetPoint) {
        strip.scrollLeft -= resetPoint;
      }
      strip.scrollBy({ left: 1, behavior: "auto" });
    }, 24);
    return () => clearInterval(interval);
  }, [isTrustStripPaused]);

  // ===== GEO LOCATION =====
  useEffect(() => {
    const getLocation = async () => {
      const result = await getCurrentCoordinates();
      if (!result.ok) {
        console.error("Failed to get location:", result.error);
        return;
      }
      setLocation({
        lat: result.coords.lat,
        lon: result.coords.lon,
      });
    };
    getLocation();
  }, []);

  // ===== FETCH APPOINTMENTS =====
  useEffect(() => {
    const fetchAppointments = async () => {
      if (!effectiveUserId) {
        setAppointments([]);
        setUpcomingAppointments([]);
        setPastAppointments([]);
        setLoadingAppointments(false);
        return;
      }
      setLoadingAppointments(true);
      try {
        const allAppts = await fetchAllConsultationsWithDoctors(effectiveUserId);
        setAppointments(allAppts);
        const now = new Date();
        const upcoming = allAppts.filter((a) => {
          const status = (a.status || "").toLowerCase();
          const apptTime = new Date(a.appointmentTime);
          return (status === "scheduled" || status === "ongoing") && apptTime >= now;
        });
        const past = allAppts.filter((a) => {
          const status = (a.status || "").toLowerCase();
          const apptTime = new Date(a.appointmentTime);
          return status === "completed" || apptTime < now;
        });
        setUpcomingAppointments(upcoming);
        setPastAppointments(past);
      } catch (err) {
        console.error("Error fetching appointments:", err);
        setAppointments([]);
        setUpcomingAppointments([]);
        setPastAppointments([]);
      } finally {
        setLoadingAppointments(false);
      }
    };
    fetchAppointments();
  }, [effectiveUserId]);

  const getShareableRecords = useCallback(() => {
  const allRecords = [
    ...(pendingTests || []).map((test) => ({
      id: test._id,
      title: test.tests?.[0]?.testId?.name || "Lab Test",
      date: test.createdAt,
      type: "Lab Test",
      status: test.status || "New",
    })),
    ...(userDocuments || []).map((doc) => ({
      id: doc._id,
      title: doc.title || "Medical Record",
      date: doc.createdAt,
      type: "Document",
      status: "Added",
    })),
    ...(appointments || []).map((appt) => ({
      id: appt._id,
      title: appt.doctor_?.name || appt.doctor?.name || "Doctor Consultation",
      date: appt.appointmentTime,
      type: "Consultation",
      status: appt.status || "Scheduled",
    })),
    ...(prescriptions || []).map((rx) => ({
      id: rx._id,
      title: rx.medicineName || "Prescription",
      date: rx.issuedDate || rx.createdAt,
      type: "Prescription",
      status: "Rx",
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return allRecords;
}, [pendingTests, userDocuments, appointments, prescriptions]);

  // ===== FETCH CHAT SESSIONS =====
  useEffect(() => {
    let mounted = true;
    const fetchChatSessions = async () => {
      if (!userId) {
        setChatSessions([]);
        return;
      }
      setLoadingChatSessions(true);
      try {
        const response = await api.get('/api/consultations/chat-sessions', { params: { userId } });
        if (mounted) {
          setChatSessions(response.data?.consultations || []);
        }
      } catch (error) {
        console.error('Error fetching chat sessions:', error);
        if (mounted) {
          setChatSessions([]);
        }
      } finally {
        if (mounted) {
          setLoadingChatSessions(false);
        }
      }
    };
    fetchChatSessions();
    return () => {
      mounted = false;
    };
  }, [userId]);

  // ===== FETCH MEDICATIONS =====
  useEffect(() => {
    let mounted = true;

    const fetchMedications = async () => {
      try {
        setMedicationsLoading(true);
        const res = await api.get("/api/medications");
        if (mounted) setMedications(Array.isArray(res?.data?.medications) ? res.data.medications : []);
      } catch (err) {
        console.error("Error fetching medications:", err);
        if (mounted) setMedications([]);
      } finally {
        if (mounted) setMedicationsLoading(false);
      }
    };

    fetchMedications();
    return () => {
      mounted = false;
    };
  }, []);

  // ===== FETCH LAB TESTS =====
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        const testRes = await api.get("/api/labtests");
        if (mounted && testRes?.data?.tests) {
          setTests(testRes.data.tests);
        }

        if (user_id) {
          const bookingRes = await api
            .get(`/api/lab-bookings/user/${user_id}`)
            .catch((err) => {
              console.error("Error fetching lab bookings:", err);
              return null;
            });

          if (bookingRes?.data?.bookings?.length) {
            const all = bookingRes.data.bookings;
            setPendingTests(all);
          } else {
            setPendingTests([]);
            setCompletedTests([]);
          }
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [user_id]);

  // ===== FETCH VITALS & ASSESSMENT =====
  useEffect(() => {
    let mounted = true;

    const loadSnapshotHealthData = async () => {
      try {
        const [vitalsRes, assessmentRes] = await Promise.all([
          api.get("/api/vitals/latest").catch(() => null),
          api.get("/api/assessment").catch(() => null),
        ]);

        if (!mounted) return;

        if (vitalsRes?.data?.vitals) {
          setLatestVitals(vitalsRes.data.vitals);
        } else {
          setLatestVitals(null);
        }

        const assessments = assessmentRes?.data?.assessments;
        if (Array.isArray(assessments) && assessments.length > 0) {
          const latest = [...assessments].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )[0];
          setLatestAssessment(latest);
        } else {
          setLatestAssessment(null);
        }
      } catch (err) {
        console.warn("Failed to load health snapshot data", err?.message || err);
      }
    };

    loadSnapshotHealthData();
    window.addEventListener("focus", loadSnapshotHealthData);
    const unsubscribeHealthSync = onHealthDataUpdated(loadSnapshotHealthData, {
      debounceMs: 250,
    });

    return () => {
      mounted = false;
      window.removeEventListener("focus", loadSnapshotHealthData);
      unsubscribeHealthSync();
    };
  }, []);

  // ===== FETCH USER DOCUMENTS =====
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!userId) return;
      try {
        const res = await api.get(`/api/upload-health-records/${userId}`);
        if (res.data.success) {
          setUserDocuments(res.data.documents);
        }
      } catch (err) {
        console.error("Error fetching documents:", err);
      }
    };
    fetchDocuments();
  }, [userId]);

  // ===== FETCH PRESCRIPTIONS =====
  useEffect(() => {
    const fetchPrescriptions = async () => {
      if (!userId) {
        setPrescriptions([]);
        return;
      }
      try {
        const res = await api.get(`/api/prescription/patient/${userId}`);
        const items = Array.isArray(res?.data?.prescriptions)
          ? res.data.prescriptions
          : [];
        setPrescriptions(items);
      } catch (err) {
        console.error("Error fetching prescriptions:", err);
        setPrescriptions([]);
      }
    };
    fetchPrescriptions();
  }, [userId]);

  // ===== FETCH ACTIVITIES =====
  useEffect(() => {
    let mounted = true;
    const loadActivities = async () => {
      setActivitiesLoading(true);
      try {
        const res = await api.get("/api/activities/activities");
        if (!mounted) return;

        if (Array.isArray(res.data)) {
          const formatted = res.data
            .filter((act) => {
              const message = act.message?.toLowerCase() || "";
              return (
                !message.includes("sign") &&
                !message.includes("logged in") &&
                !message.includes("read") &&
                !message.includes("blog")
              );
            })
            .map((act) => ({
              id: act._id,
              title: act.message,
              subtitle: new Date(act.createdAt).toLocaleString(),
              status: "Completed",
              icon: <Activity size={20} className="accent" />,
            }));
          setActivities(formatted);
        } else {
          setActivities([]);
        }
      } catch (err) {
        console.error("Failed to load activities", err);
        setActivities([]);
      } finally {
        if (mounted) setActivitiesLoading(false);
      }
    };
    loadActivities();
    return () => {
      mounted = false;
    };
  }, []);

  // ===== FETCH TRANSACTIONS =====
  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const transactionsRes = await Services.wallet.getTransactions({ limit: 5 });
        if (transactionsRes.transactions) {
          setTransactions(transactionsRes.transactions || []);
        }
      } catch (err) {
        console.error("Error fetching transactions:", err);
      }
    };
    fetchTransactions();
  }, []);

  // ===== COMPUTED VALUES =====
  const latestAppointment = [...(appointments || [])]
    .sort((a, b) => new Date(b.appointmentTime) - new Date(a.appointmentTime))[0];

  const latestLabBooking = [...(pendingTests || [])]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

  const safePendingTests = Array.isArray(pendingTests) ? pendingTests : [];
  const labRecords = safePendingTests.map((test) => ({
    title: test?.tests?.[0]?.testId?.name || "Lab Test",
    date: test?.createdAt,
    status: test?.status || "New",
  }));

  const safeUpcomingAppointments = Array.isArray(upcomingAppointments)
    ? upcomingAppointments
    : [];
  const safePastAppointments = Array.isArray(pastAppointments)
    ? pastAppointments
    : [];

  const consultationRecords = [...safeUpcomingAppointments, ...safePastAppointments]
    .sort((a, b) => new Date(b?.appointmentTime) - new Date(a?.appointmentTime))
    .slice(0, 6)
    .map((appt) => ({
      title: appt?.doctor_?.name || appt?.doctor?.name || "Doctor Consultation",
      date: appt?.appointmentTime,
      status: appt?.status || "Scheduled",
    }));

  const safePrescriptions = Array.isArray(prescriptions) ? prescriptions : [];

  const groupedPrescriptionRecords = useMemo(() => {
    const groups = new Map();

    safePrescriptions.forEach((rx) => {
      const issuedDate = rx?.issuedDate || rx?.createdAt || new Date().toISOString();
      const issuedDay = new Date(issuedDate).toISOString().slice(0, 10);
      const key = rx?.consultationId || `date-${issuedDay}`;

      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          consultationId: rx?.consultationId || null,
          issuedDate,
          doctorName: rx?.doctorName || "Doctor",
          diagnosis: rx?.diagnosis || "",
          instructions: rx?.instructions || "",
          followUpDate: rx?.followUpDate || null,
          medicines: [],
        });
      }

      const group = groups.get(key);
      if (new Date(issuedDate) > new Date(group.issuedDate)) {
        group.issuedDate = issuedDate;
      }

      group.medicines.push({
        _id: rx?._id,
        medicineName: rx?.medicineName || "Medicine",
        dosage: rx?.dosage || "",
        frequency: rx?.frequency || "",
        duration: rx?.duration || "",
      });

      if (!group.instructions && rx?.instructions) group.instructions = rx.instructions;
      if (!group.diagnosis && rx?.diagnosis) group.diagnosis = rx.diagnosis;
      if (!group.followUpDate && rx?.followUpDate) group.followUpDate = rx.followUpDate;
      if ((!group.doctorName || group.doctorName === "Doctor") && rx?.doctorName) {
        group.doctorName = rx.doctorName;
      }
    });

    return Array.from(groups.values()).sort(
      (a, b) => new Date(b.issuedDate) - new Date(a.issuedDate)
    );
  }, [safePrescriptions]);

  const safeUserDocuments = Array.isArray(userDocuments) ? userDocuments : [];

  const prescriptionRecords = safePrescriptions.map((rx) => ({
    title: rx?.medicineName || "Prescription",
    date: rx?.issuedDate || rx?.createdAt,
  }));

  const documentRecords = safeUserDocuments.map((doc) => ({
    title: doc?.title || "Medical Record",
    date: doc?.createdAt,
  }));

  // ===== INDEXEDDB HELPERS =====
  const saveOffline = useCallback(async (doc) => {
    if (!doc || !doc.fileUrl) return false;
    try {
      const resp = await fetch(doc.fileUrl, { mode: "cors" });
      const blob = await resp.blob();

      const openReq = indexedDB.open("qureo-offline-docs", 1);
      openReq.onupgradeneeded = () => {
        const db = openReq.result;
        if (!db.objectStoreNames.contains("docs")) db.createObjectStore("docs");
      };

      return await new Promise((resolve, reject) => {
        openReq.onsuccess = () => {
          const db = openReq.result;
          const tx = db.transaction("docs", "readwrite");
          const store = tx.objectStore("docs");
          const putReq = store.put(blob, `offline-doc-${doc._id}`);

          putReq.onsuccess = () => {
            setOfflineSaved(true);
            resolve(true);
          };
          putReq.onerror = (e) => reject(e);
        };
        openReq.onerror = (e) => reject(e);
      });
    } catch (err) {
      console.error("Save offline failed", err);
      return false;
    }
  }, []);

  const checkIfOffline = useCallback(async (doc) => {
    if (!doc) return false;
    try {
      const openReq = indexedDB.open("qureo-offline-docs", 1);
      return await new Promise((resolve) => {
        openReq.onsuccess = () => {
          const db = openReq.result;
          if (!db.objectStoreNames.contains("docs")) return resolve(false);
          const tx = db.transaction("docs", "readonly");
          const store = tx.objectStore("docs");
          const getReq = store.get(`offline-doc-${doc._id}`);
          getReq.onsuccess = () => {
            resolve(!!getReq.result);
          };
          getReq.onerror = () => resolve(false);
        };
        openReq.onerror = () => resolve(false);
      });
    } catch (err) {
      return false;
    }
  }, []);

  // ===== GROUP DOCUMENTS BY TIME =====
  const groupDocumentsByTime = useCallback((docs = []) => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const threeMonthsAgo = new Date(startOfMonth);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    const thisMonth = [];
    const last3Months = [];
    const earlier = [];

    docs.forEach((doc) => {
      const dt = doc.createdAt ? new Date(doc.createdAt) : null;
      if (!dt) {
        earlier.push(doc);
        return;
      }
      if (dt >= startOfMonth) thisMonth.push(doc);
      else if (dt >= threeMonthsAgo) last3Months.push(doc);
      else earlier.push(doc);
    });

    const sortDesc = (arr) =>
      arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return {
      thisMonth: sortDesc(thisMonth),
      last3Months: sortDesc(last3Months),
      earlier: sortDesc(earlier),
    };
  }, []);

  // ===== GENERATE PREVIEW INFO =====
  const generatePreviewInfo = useCallback((doc = {}) => {
    const what = doc.title || "Health record";
    const why = doc.description
      ? doc.description.split(". ")[0]
      : "Important health information worth keeping.";

    const keyValues = [];
    if (doc.createdAt)
      keyValues.push({ k: "Uploaded", v: new Date(doc.createdAt).toLocaleDateString() });
    if (doc.fileType) keyValues.push({ k: "Type", v: doc.fileType });
    if (doc.size) keyValues.push({ k: "Size", v: `${Math.round(doc.size / 1024)} KB` });
    if (doc.tag) keyValues.push({ k: "Tag", v: doc.tag });

    const note =
      doc.description && doc.description.length > 0
        ? doc.description
        : "No additional notes.";

    return { what, why, keyValues, note };
  }, []);

  // ===== CLOUDINARY UPLOAD =====
  const uploadToCloudinary = useCallback(async (file) => {
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", "profile_preset");
    data.append("cloud_name", "dp09q5por");

    try {
      setUploading(true);
      const res = await fetch(
        "https://api.cloudinary.com/v1_1/dp09q5por/auto/upload",
        {
          method: "POST",
          body: data,
        }
      );
      const json = await res.json();
      if (json.secure_url) {
        setFileUrl(json.secure_url);
        alert("File uploaded successfully!");
        return json.secure_url;
      } else {
        alert("Upload failed. Please try again.");
        return null;
      }
    } catch (err) {
      console.error("Upload error:", err);
      alert("Error uploading image.");
      return null;
    } finally {
      setUploading(false);
    }
  }, []);

  // ===== HANDLE SUBMIT =====
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!fileUrl) {
        alert("Please wait until the file finishes uploading.");
        return;
      }

      try {
        const body = {
          user_id: user_id,
          title: formData.title,
          description: formData.description,
          fileUrl: fileUrl,
          fileType: formData.file?.type || "document",
        };

        const res = await api.post("/api/upload-health-records/upload", body);
        const result = res.data;
        if (result.success) {
          alert("Record saved successfully!");
          setShowModal(false);
          setFormData({ title: "", description: "", file: null });
          setFileUrl("");

          try {
            await api.post("/api/activities/add-activity", {
              message: "Uploaded a health record document",
            });
          } catch (err) {
            console.error("Failed to log document upload activity", err);
          }

          if (addNotification) {
            addNotification({
              type: "record_added",
              title: "Health Record Saved",
              body: `"${formData.title}" has been added to your health records`,
              icon: "📄",
            });
          }

          // Refresh documents
          const docRes = await api.get(`/api/upload-health-records/${userId}`);
          if (docRes.data.success) {
            setUserDocuments(docRes.data.documents);
          }
        } else {
          alert("Upload failed.");
        }
      } catch (err) {
        console.error("Error saving record:", err);
        alert("Error saving record.");
      }
    },
    [fileUrl, formData, user_id, userId, addNotification]
  );

  // ===== HANDLE DOWNLOAD =====
  const handleDownload = useCallback(async () => {
    try {
      const url = selectedDoc.fileUrl;
      const urlParts = url.split("/");
      let fileName = urlParts[urlParts.length - 1].split("?")[0];

      if (!fileName.toLowerCase().includes(selectedDoc.title.toLowerCase())) {
        const ext = fileName.split(".").pop();
        fileName = `${selectedDoc.title.replace(/\s+/g, "_")}.${ext}`;
      }

      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    }
  }, [selectedDoc]);

  // ===== HANDLE SHARE =====
  const handleShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: selectedDoc.title,
          text: selectedDoc.description || "Check out this document",
          url: selectedDoc.fileUrl,
        });
      } catch (err) {
        console.log("Share canceled:", err);
      }
    } else {
      await navigator.clipboard.writeText(selectedDoc.fileUrl);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    }
  }, [selectedDoc]);

  // ===== DOWNLOAD HEALTH SUMMARY =====
  const downloadHealthSummary = useCallback(async () => {
    let profile = {};
    let assessmentRes = { data: { assessments: [] } };
    let labBookingsRes = { data: { bookings: [] } };
    let appointmentsRes = { data: { consultations: [] } };
    let documentsRes = { data: { documents: [] } };

    try {
      setDownloadingPDF(true);

      // Fetch all data
      try {
        const profileRes = await api.get("/api/profile");
        profile = profileRes.data.profile || {};
        console.log(profileRes, "profile res")
      } catch (e) {
        profile = {};
      }

      try {
        assessmentRes = await api.get("/api/assessment");
      } catch (e) {
        assessmentRes = { data: { assessments: [] } };
      }

      try {
        labBookingsRes = await api.get(`/api/lab-bookings/user/${user_id}`);
      } catch (e) {
        labBookingsRes = { data: { bookings: [] } };
      }

      try {
        appointmentsRes = await api.get("/appointments");
      } catch (e) {
        appointmentsRes = { data: { consultations: [] } };
      }

      try {
        documentsRes = await api.get(`/api/upload-health-records/${userId}`);
        console.log(documentsRes, "document")
      } catch (e) {
        documentsRes = { data: { documents: [] } };
        console.log(documentsRes, "document")
      }

      // PDF setup
      const pdf = new jsPDF();
      const margin = 15;
      const maxWidth = 180;
      let yPosition = 20;
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Helper functions
      const addText = (text, options = {}) => {
        const { fontSize = 10, isBold = false, color = [0, 0, 0], indent = 0 } = options;
        pdf.setFontSize(fontSize);
        pdf.setTextColor(...color);
        pdf.setFont(undefined, isBold ? "bold" : "normal");
        pdf.text(text, margin + indent, yPosition);
        yPosition += 6;
      };

      const addSectionHeader = (text) => {
        yPosition += 4;
        addText(text, { fontSize: 12, isBold: true, color: [0, 102, 204] });
        yPosition += 2;
      };

      // Header
      yPosition += 3;
      addText("Qureo Health Records Summary", {
        fontSize: 16,
        isBold: true,
        color: [0, 51, 102],
      });
      addText(`Generated: ${new Date().toLocaleString()}`, {
        fontSize: 9,
        color: [100, 100, 100],
      });
      yPosition += 8;

      // Profile
      addSectionHeader("Patient Information");
      if (profile.fullName) addText(`Name: ${profile.fullName}`, { fontSize: 10, indent: 5 });
      if (profile.email) addText(`Email: ${profile.email}`, { fontSize: 10, indent: 5 });
      if (profile.phone) addText(`Phone: ${profile.phone}`, { fontSize: 10, indent: 5 });
      if (profile.dob)
        addText(`Date of Birth: ${new Date(profile.dob).toLocaleDateString()}`, {
          fontSize: 10,
          indent: 5,
        });

      // Medical Info
      yPosition += 3;
      addText("Medical Information:", {
        fontSize: 10,
        isBold: true,
        color: [50, 50, 50],
        indent: 5,
      });
      if (profile.allergies?.length > 0) {
        addText(`Allergies: ${profile.allergies.join(", ")}`, {
          fontSize: 10,
          indent: 10,
        });
      }
      if (profile.medications?.length > 0) {
        addText(`Current Medications: ${profile.medications.join(", ")}`, {
          fontSize: 10,
          indent: 10,
        });
      }

      // Emergency Contact
      if (profile.emergencyContact?.name) {
        yPosition += 3;
        addText("Emergency Contact:", {
          fontSize: 10,
          isBold: true,
          color: [50, 50, 50],
          indent: 5,
        });
        addText(
          `${profile.emergencyContact.name} (${profile.emergencyContact.relation || "Relation"}) - ${profile.emergencyContact.phone}`,
          { fontSize: 10, indent: 10 }
        );
      }

      // Health Assessment
      if (assessmentRes.data?.assessments?.length > 0) {
        addSectionHeader("Health Assessment & Vitals");
        assessmentRes.data.assessments.forEach((assessment, idx) => {
          if (idx > 0) yPosition += 3;
          addText(`Assessment ${idx + 1}`, {
            fontSize: 11,
            isBold: true,
            indent: 5,
          });
          addText(
            `Date: ${new Date(assessment.createdAt).toLocaleDateString()} at ${new Date(
              assessment.createdAt
            ).toLocaleTimeString()}`,
            { fontSize: 10, indent: 10, color: [100, 100, 100] }
          );
          addText(`Overall Score: ${assessment.score || "N/A"}/100`, {
            fontSize: 11,
            isBold: true,
            indent: 10,
            color: [0, 102, 204],
          });

          if (assessment.heightCm || assessment.weightKg) {
            yPosition += 2;
            addText("Physical Measurements:", {
              fontSize: 10,
              isBold: true,
              color: [50, 50, 50],
              indent: 10,
            });
            if (assessment.heightCm)
              addText(`Height: ${assessment.heightCm} cm`, {
                fontSize: 10,
                indent: 15,
              });
            if (assessment.weightKg)
              addText(`Weight: ${assessment.weightKg} kg`, {
                fontSize: 10,
                indent: 15,
              });
            if (assessment.heightCm && assessment.weightKg) {
              const bmi = (assessment.weightKg / ((assessment.heightCm / 100) ** 2)).toFixed(
                1
              );
              addText(`BMI: ${bmi}`, { fontSize: 10, indent: 15 });
            }
          }

          if (assessment.fitnessLevel || assessment.sleepLevel || assessment.mood) {
            yPosition += 2;
            addText("Health Indicators:", {
              fontSize: 10,
              isBold: true,
              color: [50, 50, 50],
              indent: 10,
            });
            if (assessment.fitnessLevel)
              addText(`Fitness Level: ${assessment.fitnessLevel}`, {
                fontSize: 10,
                indent: 15,
              });
            if (assessment.sleepLevel)
              addText(`Sleep Quality: ${assessment.sleepLevel}`, {
                fontSize: 10,
                indent: 15,
              });
            if (assessment.mood)
              addText(`Mood: ${assessment.mood}`, { fontSize: 10, indent: 15 });
          }
        });
      } else {
        addSectionHeader("Health Assessment");
        addText("No health assessment records found", {
          fontSize: 10,
          color: [150, 150, 150],
          indent: 5,
        });
      }

      // Consultations
      const appointmentRecords = (
        Array.isArray(appointmentsRes?.data)
          ? appointmentsRes.data
          : Array.isArray(appointmentsRes?.data?.consultations)
          ? appointmentsRes.data.consultations
          : []
      ).filter((appt) => {
        const patient = appt?.patient;
        const patientId =
          (typeof patient === "object" ? patient?._id : patient) ||
          appt?.patientId ||
          appt?.user ||
          appt?.userId;
        return String(patientId || "") === String(userId || "");
      });

      if (appointmentRecords.length > 0) {
        addSectionHeader("Consultations");
        const sortedAppointments = appointmentRecords
          .slice()
          .sort((a, b) => new Date(b.appointmentTime) - new Date(a.appointmentTime));

        sortedAppointments.forEach((appt, index) => {
          const doctorName = appt.doctor_?.name || appt.doctor?.name || "Unknown Doctor";
          addText(`${index + 1}. ${doctorName}`, {
            fontSize: 11,
            isBold: true,
            indent: 5,
          });
          const specialty = appt.doctor_?.specialty || appt.doctor?.specialty || "N/A";
          addText(`Specialty: ${specialty}`, { fontSize: 10, indent: 10 });
          addText(`Date: ${new Date(appt.appointmentTime).toLocaleDateString()}`, {
            fontSize: 10,
            indent: 10,
          });
          addText(`Status: ${appt.status || "Scheduled"}`, {
            fontSize: 10,
            indent: 10,
            color: appt.status === "completed" ? [22, 163, 74] : [245, 158, 11],
          });
          if (appt.reason) addText(`Reason: ${appt.reason}`, { fontSize: 10, indent: 10 });
          yPosition += 3;
        });
      } else {
        addSectionHeader("Consultations");
        addText("No consultation records found", {
          fontSize: 10,
          color: [150, 150, 150],
          indent: 5,
        });
      }

      // Lab Tests
      if (labBookingsRes.data?.bookings?.length > 0) {
        addSectionHeader("Laboratory Tests");
        labBookingsRes.data.bookings.forEach((booking, index) => {
          addText(`${index + 1}. Booking ID: ${booking._id?.substring(0, 8) || "N/A"}`, {
            fontSize: 11,
            isBold: true,
            indent: 5,
          });
          addText(`Date: ${new Date(booking.bookingDate).toLocaleDateString()}`, {
            fontSize: 10,
            indent: 10,
          });
          addText(
            `Status: ${booking.status?.replace("_", " ").toUpperCase() || "Pending"}`,
            {
              fontSize: 10,
              indent: 10,
            }
          );
          if (booking.tests?.length > 0) {
            booking.tests.forEach((test, testIndex) => {
              addText(
                `Test ${testIndex + 1}: ${test.testId?.name || test.name || "Lab Test"}`,
                {
                  fontSize: 10,
                  indent: 15,
                }
              );
              if (test.result?.status)
                addText(`Result: ${test.result.status}`, {
                  fontSize: 9,
                  indent: 20,
                  color: test.result.status === "ready" ? [22, 163, 74] : [245, 158, 11],
                });
            });
          }
          yPosition += 2;
        });
      } else {
        addSectionHeader("Laboratory Tests");
        addText("No lab test bookings found", {
          fontSize: 10,
          color: [150, 150, 150],
          indent: 5,
        });
      }

      // Prescriptions
      if (prescriptions && prescriptions.length > 0) {
        addSectionHeader("Prescriptions");
        prescriptions.forEach((rx, index) => {
          addText(`${index + 1}. ${rx.medicineName || "Medicine"}`, {
            fontSize: 11,
            isBold: true,
            indent: 5,
          });
          if (rx.dosage) addText(`Dosage: ${rx.dosage}`, { fontSize: 10, indent: 10 });
          if (rx.frequency)
            addText(`Frequency: ${rx.frequency}`, { fontSize: 10, indent: 10 });
          if (rx.duration)
            addText(`Duration: ${rx.duration}`, { fontSize: 10, indent: 10 });
          if (rx.issuedDate)
            addText(`Issued: ${new Date(rx.issuedDate).toLocaleDateString()}`, {
              fontSize: 9,
              color: [100, 100, 100],
              indent: 10,
            });
          yPosition += 3;
        });
      }

      // Documents
if (documentsRes.data?.documents?.length > 0) {
  addSectionHeader("Documents");

  documentsRes.data.documents.slice(0, 20).forEach((doc, index) => {
    // Add document image
    if (doc.fileUrl) {
      const imageUrl =  doc.fileUrl;

      try {
        // Keep image dimensions small enough for the PDF
        const imageWidth = 50;
        const imageHeight = 35;

        // Make sure there is enough space on the page
        if (yPosition + imageHeight + 20 > pageHeight) {
          doc.addPage();
          yPosition = 20;
        }

        doc.addImage(
          imageUrl,
          "JPEG",
          15,
          yPosition,
          imageWidth,
          imageHeight
        );

        yPosition += imageHeight + 5;
      } catch (error) {
        console.error("Failed to add document image:", error);
      }
    }

    addText(`${index + 1}. ${doc.title || "Document"}`, {
      fontSize: 10,
      isBold: true,
      indent: 5,
    });

    if (doc.description) {
      const descLines = doc.description.split("\n");

      descLines.forEach((line) => {
        addText(`${line}`, {
          fontSize: 9,
          indent: 10,
        });
      });
    }

    const uploadDate = doc.createdAt
      ? new Date(doc.createdAt).toLocaleDateString()
      : doc.uploadedAt
      ? new Date(doc.uploadedAt).toLocaleDateString()
      : "Date unknown";

    addText(`Uploaded: ${uploadDate}`, {
      fontSize: 9,
      indent: 10,
      color: [100, 100, 100],
    });

    yPosition += 5;
  });

  if (documentsRes.data.documents.length > 20) {
    addText(
      `...and ${documentsRes.data.documents.length - 20} more documents`,
      {
        fontSize: 9,
        color: [100, 100, 100],
        indent: 5,
      }
    );
  }
} else {
  addSectionHeader("Documents");

  addText("No medical documents found", {
    fontSize: 10,
    color: [150, 150, 150],
    indent: 5,
  });
}
      // Footer
      yPosition = pageHeight - 20;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, yPosition, margin + maxWidth, yPosition);
      addText(
        "This is an official health records summary from Qureo SmartClinic Platform. Please keep this confidential.",
        {
          fontSize: 8,
          color: [150, 150, 150],
        }
      );
      addText(`Report ID: ${Date.now()}`, { fontSize: 7, color: [150, 150, 150] });

      // Save PDF
      const fileName = `Health_Records_${user_id?.substring(0, 8)}_${new Date()
        .toISOString()
        .split("T")[0]}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error("Error downloading health summary:", err);
      alert("Failed to download health summary. Please try again.");
    } finally {
      setDownloadingPDF(false);
    }
  }, [prescriptions, userId, user_id]);

  // ===== FOLDER CARDS =====
  const folderCards = [
    {
      id: "Visits",
      label: "Consultations",
      count: appointments.length,
      iconBg: "#e8ddff",
      iconColor: "#7c3aed",
    },
    {
      id: "Labs",
      label: "Lab Tests",
      count: pendingTests.length,
      iconBg: "#ffdde0",
      iconColor: "#ef4444",
    },
    {
      id: "Prescriptions",
      label: "Prescriptions",
      count: prescriptions.length,
      iconBg: "#e5e7eb",
      iconColor: "#6b7280",
    },
    {
      id: "Medications",
      label: "Medications",
      count: medications.length,
      iconBg: "#dcfce7",
      iconColor: "#16a34a",
    },
    {
      id: "Imaging",
      label: "Imaging",
      count: 0,
      iconBg: "#fed7e2",
      iconColor: "#db2777",
    },
    {
      id: "Documents",
      label: "Documents",
      count: userDocuments.length,
      iconBg: "#fef3c7",
      iconColor: "#d97706",
    },
    {
      id: "Notes",
      label: "Health Notes",
      count: activities.length,
      iconBg: "#e5e7eb",
      iconColor: "#9ca3af",
    },
  ];

  // ===== RENDER =====
  return (
    <div
      className="container"
      style={{
        background: "var(--bg)",
        minHeight: "100vh",
        fontFamily: "Inter, sans-serif",
        paddingBottom: 80,
      }}
    >
      <div style={styles.header}>
        <FeatureHeader
          title="My Health Records"
          subtitle="Your health records all in one place."
          onBack={() => window.history.back()}
          containerStyle={{ padding: 0 }}
        />

        <div style={{ marginTop: "10px" }}>
          <SearchRow
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            navigate={navigate}
          />
        </div>
      </div>

      {/* FOLDER GRID */}
      {activeTab === "All" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          {folderCards.map((card) => {
            let icon = null;

            if (card.id === "Visits")
              icon = (
                <span
                  style={{
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: "#e0f2fe",
                    color: card.iconColor,
                    marginBottom: 4,
                  }}
                >
                  <Stethoscope size={17} color={card.iconColor} />
                </span>
              );

            if (card.id === "Labs")
              icon = (
                <span
                  style={{
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: "#f3e8ff",
                    color: card.iconColor,
                    marginBottom: 4,
                  }}
                >
                  <FlaskConical size={17} color={card.iconColor} />
                </span>
              );

            if (card.id === "Prescriptions")
              icon = (
                <span
                  style={{
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: "#fef9c3",
                    color: card.iconColor,
                    marginBottom: 4,
                  }}
                >
                  <ClipboardX size={17} color={card.iconColor} />
                </span>
              );

            if (card.id === "Medications")
              icon = (
                <span
                  style={{
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: "#dcfce7",
                    color: card.iconColor,
                    marginBottom: 4,
                  }}
                >
                  <Pill size={17} color={card.iconColor} />
                </span>
              );

            if (card.id === "Imaging")
              icon = <Paperclip size={17} color={card.iconColor} />;
            if (card.id === "Documents")
              icon = <FileText size={17} color={card.iconColor} />;
            if (card.id === "Notes") icon = <BookOpen size={17} color={card.iconColor} />;

            let badge = null;

            if (
              card.id === "Labs" &&
              pendingTests &&
              pendingTests.filter((t) => t.status === "new").length > 0
            ) {
              badge = (
                <span
                  style={{
                    background: "#ede9fe",
                    color: "#7c3aed",
                    fontWeight: 600,
                    fontSize: 8,
                    borderRadius: 8,
                    padding: "2px 7px",
                    marginTop: 5,
                    display: "inline-flex",
                    alignItems: "center",
                    width: "fit-content",
                  }}
                >
                  ✦ {pendingTests.filter((t) => t.status === "new").length} new
                </span>
              );
            }

            if (
              card.id === "Prescriptions" &&
              prescriptions &&
              prescriptions.filter((p) => p.status === "active").length > 0
            ) {
              badge = (
                <span
                  style={{
                    background: "#d1fae5",
                    color: "#059669",
                    fontWeight: 600,
                    fontSize: 8,
                    borderRadius: 8,
                    padding: "2px 7px",
                    marginTop: 5,
                    display: "inline-flex",
                    alignItems: "center",
                    width: "fit-content",
                  }}
                >
                  1 active
                </span>
              );
            }

            return (
              <div
                key={card.id}
                onClick={() => setActiveTab(card.id)}
                style={{
                  background: "#f8f8f6",
                  borderRadius: 18,
                  padding: "13px 16px",
                  cursor: "pointer",
                  position: "relative",
                  border: "1px solid #f1f1ef",
                  minHeight: 96,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                  transition: "all 0.2s ease",
                  overflow: "hidden",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start" }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: card.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 10,
                      flexShrink: 0,
                    }}
                  >
                    {icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div
                        style={{
                          flex: 1,
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#111827",
                          lineHeight: 1.2,
                        }}
                      >
                        {card.label}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "#9ca3af",
                        fontWeight: 500,
                        marginTop: 3,
                      }}
                    >
                      {card.id === "Visits"
                        ? `${card.count} record${card.count === 1 ? "" : "s"}`
                        : card.id === "Labs"
                        ? `${card.count} result${card.count === 1 ? "" : "s"}`
                        : card.id === "Prescriptions"
                        ? `${card.count} prescription${card.count === 1 ? "" : "s"}`
                          : card.id === "Medications"
                          ? `${card.count} medication${card.count === 1 ? "" : "s"}`
                        : card.id === "Imaging"
                        ? `${card.count} stud${card.count === 1 ? "y" : "ies"}`
                        : card.id === "Documents"
                        ? `${card.count} file${card.count === 1 ? "" : "s"}`
                        : card.id === "Notes"
                        ? `${card.count} note${card.count === 1 ? "" : "s"}`
                        : ""}
                    </div>
                    {badge}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 8,
                  }}
                >
                  <div
                    style={{
                      color: "var(--primary)",
                      fontSize: 14,
                      fontWeight: 700,
                      letterSpacing: 2,
                      lineHeight: 1,
                    }}
                  >
                    <ChevronRight
                      size={15}
                      color="var(--primary)"
                      strokeWidth={2.1}
                      style={{ flexShrink: 0 }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DETAIL VIEWS */}
      {activeTab !== "All" && (
        <div style={{ padding: 16, position: "relative" }}>
          <button
            onClick={() => setActiveTab("All")}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              background: "none",
              border: "none",
              cursor: "pointer",
              zIndex: 10,
              padding: 4,
            }}
            aria-label="Close folder view"
          >
            <X size={20} />
          </button>

          {/* VISITS */}
          {activeTab === "Visits" && (
            <>
              <h3
                style={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  marginBottom: 12,
                  color: "#1a1a1a",
                }}
              >
                Upcoming Consultations
              </h3>
              {loadingAppointments ? (
                <p style={{ color: "#888" }}>Loading visits...</p>
              ) : upcomingAppointments.length === 0 ? (
                <p style={{ color: "#888" }}>No upcoming consultations.</p>
              ) : (
                upcomingAppointments
                  .slice()
                  .sort((a, b) => new Date(b.appointmentTime) - new Date(a.appointmentTime))
                  .filter(
                    (appt) =>
                      searchQuery === "" ||
                      appt.doctor?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      appt.reason?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      appt.doctor?.specialty?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((appt) => (
                    <div
                      key={appt._id}
                      style={{
                        background: "#fff",
                        padding: 14,
                        borderRadius: 16,
                        marginBottom: 12,
                        boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                            {appt.doctor?.name || "Unknown Doctor"}
                          </div>
                          <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>
                            {appt.doctor?.specialty || ""}
                          </div>
                          {appt.clinic && (
                            <div style={{ fontSize: 12, color: "#888" }}>{appt.clinic}</div>
                          )}
                          {appt.reason && (
                            <div style={{ fontSize: 13, color: "#444", marginTop: 6 }}>
                              {appt.reason}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>
                            {new Date(appt.appointmentTime).toLocaleDateString()}
                          </div>
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 12,
                              color: appt.status === "completed" ? "#16a34a" : "#f59e0b",
                            }}
                          >
                            {appt.status || "Scheduled"}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                        <button
                          onClick={() => {
                            setSelectedAppt(appt);
                            setShowVisitModal(true);
                          }}
                          style={{
                            flex: 1,
                            padding: "9px 0",
                            borderRadius: 10,
                            border: "1px solid #e6e6e6",
                            background: "#fff",
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          View
                        </button>
                        <button
                          onClick={() =>
                            navigate(`/doctor-details/${appt.doctor?._id || ""}`)
                          }
                          style={{
                            flex: 1,
                            padding: "9px 0",
                            borderRadius: 10,
                            border: "none",
                            background: "#7c3aed",
                            color: "#fff",
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          Doctor
                        </button>
                      </div>
                    </div>
                  ))
              )}

              <h3
                style={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  margin: "24px 0 12px 0",
                  color: "#1a1a1a",
                }}
              >
                Past Consultations
              </h3>
              {loadingAppointments ? (
                <p style={{ color: "#888" }}>Loading visits...</p>
              ) : pastAppointments.length === 0 ? (
                <p style={{ color: "#888" }}>No past consultations.</p>
              ) : (
                pastAppointments
                  .slice()
                  .sort((a, b) => new Date(b.appointmentTime) - new Date(a.appointmentTime))
                  .filter(
                    (appt) =>
                      searchQuery === "" ||
                      appt.doctor?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      appt.reason?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      appt.doctor?.specialty?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((appt) => (
                    <div
                      key={appt._id}
                      style={{
                        background: "#fff",
                        padding: 14,
                        borderRadius: 16,
                        marginBottom: 12,
                        boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                            {appt.doctor?.name || "Unknown Doctor"}
                          </div>
                          <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>
                            {appt.doctor?.specialty || ""}
                          </div>
                          {appt.clinic && (
                            <div style={{ fontSize: 12, color: "#888" }}>{appt.clinic}</div>
                          )}
                          {appt.reason && (
                            <div style={{ fontSize: 13, color: "#444", marginTop: 6 }}>
                              {appt.reason}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>
                            {new Date(appt.appointmentTime).toLocaleDateString()}
                          </div>
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 12,
                              color: appt.status === "completed" ? "#16a34a" : "#f59e0b",
                            }}
                          >
                            {appt.status || "Scheduled"}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                        <button
                          onClick={() => {
                            setSelectedAppt(appt);
                            setShowVisitModal(true);
                          }}
                          style={{
                            flex: 1,
                            padding: "9px 0",
                            borderRadius: 10,
                            border: "1px solid #e6e6e6",
                            background: "#fff",
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          View
                        </button>
                        <button
                          onClick={() =>
                            navigate(`/doctor-details/${appt.doctor?._id || ""}`)
                          }
                          style={{
                            flex: 1,
                            padding: "9px 0",
                            borderRadius: 10,
                            border: "none",
                            background: "#7c3aed",
                            color: "#fff",
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          Doctor
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </>
          )}

          {/* LABS */}
          {activeTab === "Labs" && (
            <>
              <h3
                style={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  marginBottom: 12,
                  color: "#1a1a1a",
                }}
              >
                Lab Tests
              </h3>
              {loading ? (
                <p style={{ color: "#888" }}>Loading lab tests...</p>
              ) : pendingTests.length === 0 ? (
                <p style={{ color: "#888" }}>No lab tests found.</p>
              ) : (
                pendingTests
                  .filter(
                    (test) =>
                      searchQuery === "" ||
                      test.tests?.[0]?.testId?.name
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase())
                  )
                  .map((test) => (
                    <div
                      key={test._id}
                      style={{
                        background: "#fff",
                        padding: 14,
                        borderRadius: 16,
                        marginBottom: 12,
                        boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                            {test.tests?.[0]?.testId?.name || "Lab Test"}
                          </div>
                          {test.tests?.[0]?.testId?.description && (
                            <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>
                              {test.tests[0].testId.description}
                            </div>
                          )}
                          <div style={{ fontSize: 12, color: "#888" }}>
                            Booked {new Date(test.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <div
                          style={{
                            textAlign: "right",
                            flexShrink: 0,
                            fontSize: 12,
                            color:
                              test.tests?.[0]?.result?.status === "completed"
                                ? "#16a34a"
                                : "#f59e0b",
                          }}
                        >
                          {test.tests?.[0]?.result?.status || "Pending"}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/booking-details/${test._id}`)}
                        style={{
                          marginTop: 12,
                          width: "100%",
                          padding: "9px 0",
                          borderRadius: 10,
                          border: "none",
                          background: "#7c3aed",
                          color: "#fff",
                          fontSize: 13,
                          cursor: "pointer",
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  ))
              )}
            </>
          )}

          {/* PRESCRIPTIONS */}
          {activeTab === "Prescriptions" && (
            <>
              <h3
                style={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  marginBottom: 12,
                  color: "#1a1a1a",
                }}
              >
                Prescriptions
              </h3>
              {groupedPrescriptionRecords.length === 0 ? (
                <p style={{ color: "#888" }}>No prescriptions found.</p>
              ) : (
                groupedPrescriptionRecords.map((group, i) => (
                  <div
                    key={group.id || i}
                    style={{
                      background: "#fff",
                      padding: 14,
                      borderRadius: 16,
                      marginBottom: 12,
                      boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                      {group.doctorName} prescription
                    </div>
                    <div style={{ fontSize: 13, color: "#666" }}>
                      {group.medicines.length} medicine
                      {group.medicines.length > 1 ? "s" : ""}
                    </div>
                    {group.issuedDate && (
                      <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
                        {new Date(group.issuedDate).toLocaleDateString()}
                      </div>
                    )}
                    <button
                      onClick={() => setSelectedPrescriptionGroup(group)}
                      style={{
                        marginTop: 10,
                        background: "#f3edff",
                        border: "1px solid #e4dbff",
                        borderRadius: 10,
                        padding: "8px 12px",
                        color: "#4f21de",
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      View Full Prescription
                    </button>
                  </div>
                ))
              )}
            </>
          )}

          {/* IMAGING */}
          {activeTab === "Imaging" && (
            <>
              <h3
                style={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  marginBottom: 12,
                  color: "#1a1a1a",
                }}
              >
                Medications
              </h3>
              {medicationsLoading ? (
                <p style={{ color: "#888" }}>Loading medications...</p>
              ) : medications.length === 0 ? (
                <p style={{ color: "#888" }}>No medications found.</p>
              ) : (
                medications.map((medication) => (
                  <div
                    key={medication._id}
                    style={{
                      background: "#fff",
                      padding: 14,
                      borderRadius: 16,
                      marginBottom: 12,
                      boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          background: "#dcfce7",
                          display: "grid",
                          placeItems: "center",
                          color: "#16a34a",
                          flexShrink: 0,
                        }}
                      >
                        <Pill size={18} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a1a" }}>
                          {medication.medicineName}
                        </div>
                        <div style={{ fontSize: 13, color: "#666", marginTop: 3 }}>
                          {[medication.brandName, medication.strength, medication.category].filter(Boolean).join(" • ") || "Medication"}
                        </div>
                        <div style={{ fontSize: 12, color: "#777", marginTop: 7 }}>
                          {medication.frequency || "Schedule not specified"}
                          {medication.duration ? ` • ${medication.duration}` : ""}
                        </div>
                        {Array.isArray(medication.dosages) && medication.dosages.length > 0 && (
                          <div style={{ fontSize: 12, color: "#555", marginTop: 5 }}>
                            Doses: {medication.dosages.map((dose) => dose.time).filter(Boolean).join(", ")}
                          </div>
                        )}
                        {medication.instructions && (
                          <div style={{ fontSize: 12, color: "#777", marginTop: 5 }}>
                            {medication.instructions}
                          </div>
                        )}
                      </div>
                      <span
                        style={{
                          color: medication.isActive === false ? "#9ca3af" : "#16a34a",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {medication.isActive === false ? "Inactive" : "Active"}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {/* MEDICATIONS */}
          {activeTab === "Medications" && (
            <>
              <h3
                style={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  marginBottom: 12,
                  color: "#1a1a1a",
                }}
              >
                Medications
              </h3>
              {medicationsLoading ? (
                <p style={{ color: "#888" }}>Loading medications...</p>
              ) : medications.length === 0 ? (
                <p style={{ color: "#888" }}>No medications found.</p>
              ) : (
                medications.map((medication) => (
                  <div
                    key={medication._id}
                    style={{
                      background: "#fff",
                      padding: 14,
                      borderRadius: 16,
                      marginBottom: 12,
                      boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          background: "#dcfce7",
                          display: "grid",
                          placeItems: "center",
                          color: "#16a34a",
                          flexShrink: 0,
                        }}
                      >
                        <Pill size={18} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a1a" }}>
                          {medication.medicineName}
                        </div>
                        <div style={{ fontSize: 13, color: "#666", marginTop: 3 }}>
                          {[medication.brandName, medication.strength, medication.category].filter(Boolean).join(" • ") || "Medication"}
                        </div>
                        <div style={{ fontSize: 12, color: "#777", marginTop: 7 }}>
                          {medication.frequency || "Schedule not specified"}
                          {medication.duration ? ` • ${medication.duration}` : ""}
                        </div>
                        {Array.isArray(medication.dosages) && medication.dosages.length > 0 && (
                          <div style={{ fontSize: 12, color: "#555", marginTop: 5 }}>
                            Doses: {medication.dosages.map((dose) => dose.time).filter(Boolean).join(", ")}
                          </div>
                        )}
                        {medication.instructions && (
                          <div style={{ fontSize: 12, color: "#777", marginTop: 5 }}>
                            {medication.instructions}
                          </div>
                        )}
                      </div>
                      <span
                        style={{
                          color: medication.isActive === false ? "#9ca3af" : "#16a34a",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {medication.isActive === false ? "Inactive" : "Active"}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {/* DOCUMENTS */}
          {activeTab === "Documents" &&
            (() => {
              const filteredDocs = searchQuery
                ? userDocuments.filter(
                    (d) =>
                      d.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      d.description?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                : userDocuments;
              const groups = groupDocumentsByTime(filteredDocs || []);
              const renderDoc = (doc) => (
                <div
                  key={doc._id}
                  onClick={() => {
                    setSelectedDoc(doc);
                    setViewingFull(false);
                    checkIfOffline(doc).then((saved) => setOfflineSaved(!!saved));
                  }}
                  style={{
                    background: "#fff",
                    padding: 14,
                    borderRadius: 16,
                    marginBottom: 10,
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{doc.title}</div>
                  {doc.description && (
                    <p style={{ fontSize: 12, color: "#666", margin: "4px 0 0" }}>
                      {doc.description}
                    </p>
                  )}
                  <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>
                    {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : ""}
                  </div>
                </div>
              );
              return (
                <>
                  <h3
                    style={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      marginBottom: 12,
                      color: "#1a1a1a",
                    }}
                  >
                    Documents
                  </h3>
                  {filteredDocs.length === 0 ? (
                    <p style={{ color: "#888" }}>No documents found.</p>
                  ) : null}
                  {groups.thisMonth.length > 0 && (
                    <>
                      <h4
                        style={{ fontSize: 13, margin: "8px 0 8px", color: "#6b7280" }}
                      >
                        This Month
                      </h4>
                      {groups.thisMonth.map(renderDoc)}
                    </>
                  )}
                  {groups.last3Months.length > 0 && (
                    <>
                      <h4
                        style={{ fontSize: 13, margin: "8px 0 8px", color: "#6b7280" }}
                      >
                        Last 3 Months
                      </h4>
                      {groups.last3Months.map(renderDoc)}
                    </>
                  )}
                  {groups.earlier.length > 0 && (
                    <>
                      <h4
                        style={{ fontSize: 13, margin: "8px 0 8px", color: "#6b7280" }}
                      >
                        Earlier
                      </h4>
                      {groups.earlier.map(renderDoc)}
                    </>
                  )}
                </>
              );
            })()}

          {/* NOTES */}
          {activeTab === "Notes" && (
            <>
              <h3
                style={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  marginBottom: 12,
                  color: "#1a1a1a",
                }}
              >
                Health Notes
              </h3>
              {activitiesLoading ? (
                <p style={{ color: "#888" }}>Loading...</p>
              ) : activities.length === 0 ? (
                <p style={{ color: "#888" }}>No notes yet.</p>
              ) : (
                activities.map((act) => {
                  const meta = getActivityMeta(act.title);
                  return (
                    <div
                      key={act.id}
                      style={{
                        background: "#fff",
                        padding: 14,
                        borderRadius: 16,
                        marginBottom: 10,
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          background: meta.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {meta.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: 14,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {act.title}
                        </div>
                        <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                          {act.subtitle}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>
      )}

      {/* QUICK ACTIONS */}
      {activeTab === "All" && (
        <div
          style={{
            marginTop: 24,
            padding: "0 0px 16px",
            overflowX: "auto",
            scrollbarWidth: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 12,
              minWidth: "max-content",
            }}
          >
            <div
              onClick={() => setShowModal(true)}
              style={{
                width: 150,
                background: "#f8f8f6",
                borderRadius: 22,
                border: "1px solid #f1f1ef",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 14px",
                cursor: "pointer",
                minHeight: 82,
              }}
            >
              <Paperclip
                size={22}
                color="#7c4dff"
                strokeWidth={2.2}
                style={{ flexShrink: 0 }}
              />
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#111827",
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                  }}
                >
                  Upload Record
                </div>
                <div
                  style={{
                    fontSize: 9,
                    color: "#9ca3af",
                    marginTop: 3,
                    whiteSpace: "nowrap",
                  }}
                >
                  Add files securely
                </div>
              </div>
            </div>

          <div
  onClick={handleShareClick}
  style={{
    width: 150,
    background: "#f8f8f6",
    borderRadius: 22,
    border: "1px solid #f1f1ef",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    cursor: "pointer",
    minHeight: 82,
  }}
>
  <Share2
    size={22}
    color="#7c4dff"
    strokeWidth={2.2}
    style={{ flexShrink: 0 }}
  />
  <div>
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "#111827",
        lineHeight: 1.15,
        whiteSpace: "nowrap",
      }}
    >
      Share Records
    </div>
    <div
      style={{
        fontSize: 9,
        color: "#9ca3af",
        marginTop: 3,
        whiteSpace: "nowrap",
      }}
    >
      With doctors or family
    </div>
  </div>
</div>

            <div
              onClick={!downloadingPDF ? downloadHealthSummary : undefined}
              style={{
                minWidth: 220,
                background: "#f8f8f6",
                borderRadius: 22,
                border: "1px solid #f1f1ef",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 14px",
                cursor: downloadingPDF ? "not-allowed" : "pointer",
                opacity: downloadingPDF ? 0.7 : 1,
                minHeight: 82,
              }}
            >
              <Download
                size={22}
                color="#7c4dff"
                strokeWidth={2.2}
                style={{ flexShrink: 0 }}
              />
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#111827",
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                  }}
                >
                  {downloadingPDF ? "Fetching..." : "Download"}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    color: "#9ca3af",
                    marginTop: 3,
                    whiteSpace: "nowrap",
                  }}
                >
                  Export your records
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI CARD */}
      <Aicard
        labRecords={labRecords}
        consultationRecords={consultationRecords}
        prescriptionRecords={prescriptionRecords}
        documentRecords={documentRecords}
        latestAssessment={latestAssessment}
        latestVitals={latestVitals}
        latestLabBooking={latestLabBooking}
        latestAppointment={latestAppointment}
      />

      {/* RECENT RECORDS */}
      <RecentRecords
        pendingTests={pendingTests}
        userDocuments={userDocuments}
        appointments={appointments}
        prescriptions={prescriptions}
        navigate={navigate}
        onSeeAllClick={() => setShowAllRecordsModal(true)}
      />

      {/* ============================================================ */}
      {/* MODALS */}
      {/* ============================================================ */}


<ShareRecordsModal
  isOpen={showShareModal}
  onClose={() => {
    setShowShareModal(false);
  }}
  records={getShareableRecords()}
  chatSessions={chatSessions}
  navigate={navigate}
  onShare={() => {
    setShowShareModal(false);
  }}
/>
      {/* SEE ALL RECORDS MODAL */}
      <RecentRecordsModal
        isOpen={showAllRecordsModal}
        onClose={() => setShowAllRecordsModal(false)}
        pendingTests={pendingTests}
        userDocuments={userDocuments}
        appointments={appointments}
        prescriptions={prescriptions}
        navigate={navigate}
      />

      {/* DOCUMENT PREVIEW MODAL */}
      <AnimatePresence>
        {selectedDoc && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 3000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setSelectedDoc(null)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: 16,
                padding: 20,
                width: "90%",
                maxWidth: 420,
                maxHeight: "85vh",
                overflowY: "auto",
                position: "relative",
              }}
            >
              <button
                onClick={() => setSelectedDoc(null)}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                }}
              >
                <X size={20} />
              </button>

              <h4 style={{ fontWeight: 600 }}>{selectedDoc.title}</h4>
              <p style={{ fontSize: "0.9rem", color: "#555" }}>
                {selectedDoc.description}
              </p>
              <p style={{ fontSize: "0.8rem", color: "#999" }}>
                Uploaded on {new Date(selectedDoc.createdAt).toLocaleDateString()}
              </p>

              {!viewingFull ? (
                <div style={{ marginTop: 12 }}>
                  {(() => {
                    const p = generatePreviewInfo(selectedDoc);
                    return (
                      <div>
                        <h5 style={{ marginTop: 0, marginBottom: 6 }}>{p.what}</h5>
                        <p style={{ color: "#555", marginTop: 0 }}>{p.why}</p>

                        <div style={{ marginTop: 8 }}>
                          {p.keyValues.map((kv) => (
                            <div
                              key={kv.k}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                padding: "6px 0",
                                borderBottom: "1px dashed #eee",
                              }}
                            >
                              <span style={{ color: "#666" }}>{kv.k}</span>
                              <strong style={{ color: "#333" }}>{kv.v}</strong>
                            </div>
                          ))}
                        </div>

                        <div style={{ marginTop: 10 }}>
                          <p style={{ fontSize: "0.9rem", color: "#666" }}>
                            <strong>Notes:</strong> {p.note}
                          </p>
                        </div>

                        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                          <button
                            onClick={() => setViewingFull(true)}
                            style={{
                              flex: 1,
                              background: "#704af6",
                              color: "#fff",
                              border: "none",
                              borderRadius: 10,
                              padding: "10px 0",
                              fontWeight: 600,
                            }}
                          >
                            View full document
                          </button>
                          <button
                            onClick={handleShare}
                            style={{
                              flex: 1,
                              background: "#f1f1f1",
                              border: "none",
                              borderRadius: 10,
                              padding: "10px 0",
                              fontWeight: 600,
                            }}
                          >
                            <Share2 size={16} /> Share
                          </button>
                        </div>

                        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                         
                          <button
                            onClick={handleDownload}
                            style={{
                              flex: 1,
                              background: "#fff",
                              border: "1px solid #e6e6e6",
                              borderRadius: 10,
                              padding: "10px 0",
                              fontWeight: 600,
                            }}
                          >
                            <Download size={16} /> Download
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div style={{ marginTop: 12 }}>
                  {selectedDoc.fileUrl && selectedDoc.fileUrl.endsWith(".pdf") ? (
                    <iframe
                      src={selectedDoc.fileUrl}
                      title="PDF Preview"
                      style={{
                        width: "100%",
                        height: 340,
                        borderRadius: 8,
                        border: "1px solid #eee",
                      }}
                    />
                  ) : (
                    <img
                      src={selectedDoc.fileUrl}
                      alt={selectedDoc.title}
                      style={{ width: "100%", borderRadius: 10 }}
                    />
                  )}

                  <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                    <button
                      onClick={handleDownload}
                      style={{
                        flex: 1,
                        background: "#704af6",
                        color: "#fff",
                        border: "none",
                        borderRadius: 10,
                        padding: "10px 0",
                        fontWeight: 600,
                      }}
                    >
                      <Download size={16} /> Download
                    </button>
                    <button
                      onClick={handleShare}
                      style={{
                        flex: 1,
                        background: "#f1f1f1",
                        border: "none",
                        borderRadius: 10,
                        padding: "10px 0",
                        fontWeight: 600,
                      }}
                    >
                      <Share2 size={16} /> Share
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PRESCRIPTION DETAILS MODAL */}
      <AnimatePresence>
        {selectedPrescriptionGroup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 3000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setSelectedPrescriptionGroup(null)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: 16,
                padding: 20,
                width: "90%",
                maxWidth: 520,
                maxHeight: "85vh",
                overflowY: "auto",
                position: "relative",
              }}
            >
              <button
                onClick={() => setSelectedPrescriptionGroup(null)}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                }}
              >
                <X size={20} />
              </button>

              <h4 style={{ marginTop: 0, fontWeight: 700 }}>Full Prescription</h4>
              <div style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>
                {selectedPrescriptionGroup.doctorName} •{" "}
                {new Date(selectedPrescriptionGroup.issuedDate).toLocaleDateString()}
              </div>

              {selectedPrescriptionGroup.diagnosis && (
                <div style={{ marginBottom: 10, color: "#444" }}>
                  <strong>Diagnosis:</strong> {selectedPrescriptionGroup.diagnosis}
                </div>
              )}

              {selectedPrescriptionGroup.instructions && (
                <div style={{ marginBottom: 10, color: "#444" }}>
                  <strong>Instructions:</strong> {selectedPrescriptionGroup.instructions}
                </div>
              )}

              {selectedPrescriptionGroup.followUpDate && (
                <div style={{ marginBottom: 10, color: "#444" }}>
                  <strong>Follow-up:</strong>{" "}
                  {new Date(selectedPrescriptionGroup.followUpDate).toLocaleDateString()}
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                {selectedPrescriptionGroup.medicines.map((med, idx) => (
                  <div
                    key={med._id || idx}
                    style={{
                      border: "1px solid #eceaf3",
                      borderRadius: 12,
                      padding: 12,
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        color: "#1a1a2e",
                        marginBottom: 4,
                      }}
                    >
                      {med.medicineName}
                    </div>
                    <div style={{ fontSize: 12.5, color: "#5b5772" }}>
                      {med.dosage ? `Dosage: ${med.dosage}` : "Dosage: Not specified"}
                    </div>
                    {med.frequency && (
                      <div style={{ fontSize: 12.5, color: "#5b5772" }}>
                        Frequency: {med.frequency}
                      </div>
                    )}
                    {med.duration && (
                      <div style={{ fontSize: 12.5, color: "#5b5772" }}>
                        Duration: {med.duration}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* VISIT DETAILS MODAL */}
      <AnimatePresence>
        {showVisitModal && selectedAppt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 3000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setShowVisitModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: 16,
                padding: 20,
                width: "90%",
                maxWidth: 520,
                maxHeight: "85vh",
                overflowY: "auto",
                position: "relative",
              }}
            >
              <button
                onClick={() => setShowVisitModal(false)}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                }}
              >
                <X size={20} />
              </button>

              <h4 style={{ marginTop: 0 }}>{selectedAppt.doctor?.name || "Visit"}</h4>
              <div style={{ fontSize: 13, color: "#666" }}>
                {selectedAppt.doctor?.specialty || selectedAppt.doctor?.speciality}
              </div>
              <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600 }}>
                {new Date(selectedAppt.appointmentTime).toLocaleString()}
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: selectedAppt.status === "completed" ? "#16a34a" : "#f59e0b",
                }}
              >
                {selectedAppt.status || "Scheduled"}
              </div>

              {selectedAppt.clinic && (
                <div style={{ marginTop: 10, color: "#444" }}>
                  <strong>Clinic:</strong> {selectedAppt.clinic}
                </div>
              )}
              {selectedAppt.reason && (
                <div style={{ marginTop: 10, color: "#444" }}>
                  <strong>Reason:</strong>{" "}
                  <div style={{ marginTop: 6 }}>{selectedAppt.reason}</div>
                </div>
              )}
              {selectedAppt.notes && (
                <div style={{ marginTop: 10 }}>
                  <strong>Notes:</strong>
                  <div style={{ marginTop: 6, color: "#666" }}>{selectedAppt.notes}</div>
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button
                  onClick={() => setShowVisitModal(false)}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #e6e6e6",
                    background: "#fff",
                  }}
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    navigate(`/doctor-details/${selectedAppt.doctor?._id || ""}`);
                    setShowVisitModal(false);
                  }}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "none",
                    background: "#704af6",
                    color: "#fff",
                  }}
                >
                  Open Doctor
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* UPLOAD MODAL */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: 16,
                width: "90%",
                maxWidth: 420,
                padding: 20,
              }}
            >
              <h5 style={{ fontWeight: 600, marginBottom: 12 }}>
                Upload External Record
              </h5>

              <form onSubmit={handleSubmit}>
                <input
                  name="title"
                  placeholder="Title"
                  className="form-control mb-2"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, title: e.target.value }))
                  }
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    marginBottom: 10,
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    fontSize: 14,
                  }}
                />

                <textarea
                  name="description"
                  placeholder="Description"
                  className="form-control mb-2"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    marginBottom: 10,
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    fontSize: 14,
                    minHeight: 80,
                    resize: "vertical",
                  }}
                />

                <input
                  type="file"
                  className="form-control mb-3"
                  accept=".pdf,.jpg,.png,.jpeg,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setFormData((prev) => ({ ...prev, file }));
                      uploadToCloudinary(file);
                    }
                  }}
                  required
                  style={{
                    width: "100%",
                    padding: "8px",
                    marginBottom: 16,
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                  }}
                />

                <button
                  type="submit"
                  className="btn w-100"
                  style={{
                    width: "100%",
                    background: "#704af6",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "12px",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: uploading ? "not-allowed" : "pointer",
                    opacity: uploading ? 0.7 : 1,
                  }}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Upload"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================

const styles = {
  trustBadge: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
    marginLeft: 45,
    marginTop: -7,
  },
  trustIcon: {
    fontSize: 14,
    color: "green",
  },
  trustText: {
    fontSize: "0.85em",
    left: "-10px",
    lineHeight: "1",
  },
  header: {
    background: "var(--bg)",
    color: "var(--on-bg)",
    position: "relative",
    width: "100%",
    overflow: "visible",
    borderBottomLeftRadius: 25,
    borderBottomRightRadius: 25,
    marginBottom: 20,
    padding: "18px 0 8px",
    fontFamily: "'Poppins', sans-serif",
  },
  headerTop: {
    display: "flex",
    marginTop: 0,
    justifyContent: "space-between",
    alignItems: "center",
  },
  backArrow: {
    cursor: "pointer",
    flexShrink: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "var(--primary)",
    marginTop: 4,
  },
  headerTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: 22,
    marginLeft: 12,
    color: "var(--on-bg)",
    letterSpacing: "0.3px",
    textAlign: "center",
    lineHeight: 1.15,
  },
  cartIcon: {
    position: "relative",
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    backdropFilter: "blur(10px)",
    transition: "all 0.2s",
  },
  headerSubtitle: {
    fontSize: 12,
    marginTop: 4,
    marginLeft: 34,
    textAlign: "left",
  },
};